import React, { useMemo, useState } from 'react';
import { Chip } from '@jotpsych/ui';
import { alpha, Box, Button, Stack, Typography } from '@mui/material';

import type { RenameEntry } from '../types';
import { renameMessageFor } from '../utils/renameDigest';

export interface RenameDigestProps {
  renames: RenameEntry[];
}

/**
 * Per-clinician rename digest (GDN-2226): after the admin applies, each
 * provider whose types were consolidated gets a copy-pasteable summary —
 * the change-management artifact for communicating back to the group.
 * Pure presentation.
 */
export const RenameDigest: React.FC<RenameDigestProps> = ({ renames }) => {
  const [copiedFor, setCopiedFor] = useState<string | null>(null);
  const byProvider = useMemo(() => {
    const map = new Map<string, RenameEntry[]>();
    for (const r of renames) {
      if (!r.provider) continue;
      map.set(r.provider, [...(map.get(r.provider) ?? []), r]);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [renames]);

  if (byProvider.length === 0) return null;
  // Count what's actually listed — entries without a provider are not
  // rendered (nobody to send the note to), so they must not inflate this.
  const listedCount = byProvider.reduce((sum, [, rows]) => sum + rows.length, 0);

  const copy = (provider: string): void => {
    void navigator.clipboard.writeText(renameMessageFor(provider, renames));
    setCopiedFor(provider);
  };

  const copyAll = (): void => {
    void navigator.clipboard.writeText(
      byProvider.map(([p]) => renameMessageFor(p, renames)).join('\n\n---\n\n')
    );
    setCopiedFor('__all__');
  };

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        mb: 2,
      })}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2">
          📋 What to tell your providers
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {listedCount} renames across {byProvider.length} providers
        </Typography>
        <Button size="small" variant="text" sx={{ ml: 'auto' }} onClick={copyAll}>
          {copiedFor === '__all__' ? 'Copied ✓' : 'Copy all'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Copy each provider&apos;s note and send it — their old type names map
        to the new shared ones. Past notes are untouched.
      </Typography>
      <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
        {byProvider.map(([provider, rows]) => (
          <Box
            key={provider}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              px: 1.25,
              py: 0.75,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            })}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {provider}
              </Typography>
              <Chip
                size="xSmall"
                variant="soft"
                color="default"
                label={`${rows.length} ${rows.length === 1 ? 'rename' : 'renames'}`}
              />
              <Button
                size="small"
                variant="text"
                sx={{ ml: 'auto', flexShrink: 0 }}
                onClick={() => copy(provider)}
              >
                {copiedFor === provider ? 'Copied ✓' : 'Copy note'}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {rows.map((r) => (
                <div key={`${r.oldName}|${r.newName}`}>
                  &ldquo;{r.oldName}&rdquo; → &ldquo;{r.newName}&rdquo;
                </div>
              ))}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
